/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/DecisionLine.tsx
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-DECISION-LINE-001
 * PURPOSE: Render the single 40px (fine-pointer) pending-decision line: one
 *   status label + target, a 2px hue accent, and the caller's control
 *   cluster. Grows to DECISION_H_COARSE_MIN on coarse pointers only.
 * ROLE IN ARCHITECTURE: Presentational leaf of the triage band (plan D269-12).
 * MAIN EXPORTS: ReaderDecisionLine.
 * NON-RESPONSIBILITIES: Confirm/reject transports live in DecisionActions.
 * NOTES FOR FUTURE AI: Exactly ONE label+target renders here — never map the
 *   queue; secondary decisions belong to the Review Queue. No full box: the
 *   2px left accent + bg-muted/20 is the entire framing (premium language).
 *   change #269 C1: the inline `height: DECISION_H` style is left untouched
 *   (fine-pointer geometry + the bodyBudget.test.tsx `.style.height` pin must
 *   stay byte-for-byte). The `pointer-coarse:min-h-11` class rides ALONGSIDE
 *   it — CSS resolves min-height as a clamp on the *used* height value after
 *   cascade, so it overrides a smaller inline `height` regardless of the
 *   inline style's normally-higher specificity. Under `pointer: fine` the
 *   class is inert and the box is exactly DECISION_H, unchanged.
 */
import type { ReactNode } from 'react'

import { cn } from '../../../_internal/lib/utils'
import type { ReaderDecision } from '../model/types'
import { DECISION_H } from '../tokens'
import { decisionHue } from './hues'

export interface ReaderDecisionLineProps {
  decision: ReaderDecision
  /** Confirm/reject circles, More menu, and the +N reviews pill (adapter-owned). */
  controls?: ReactNode
}

export function ReaderDecisionLine({
  decision,
  controls,
}: ReaderDecisionLineProps) {
  const hue = decisionHue(decision.kind)
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-l-2 bg-muted/20 px-3',
        // change #269 C1: coarse-only floor so the confirm/reject circles
        // (DecisionCircles.tsx) can reach a genuine 44px box.
        'pointer-coarse:min-h-11',
        hue.accent,
      )}
      style={{ height: DECISION_H }}
      data-testid="reader-decision-line"
      data-decision-kind={decision.kind}
    >
      <span
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', hue.dot)}
        aria-hidden
      />
      <span
        className="min-w-0 flex-1 truncate text-[11px] font-medium"
        title={`${decision.label} · ${decision.target}`}
      >
        {decision.label}
        <span className="font-normal text-muted-foreground">
          {' '}
          · {decision.target}
        </span>
      </span>
      {controls && (
        <span className="flex shrink-0 items-center gap-1">{controls}</span>
      )}
    </div>
  )
}
